import { Trans, useLingui } from "@lingui/react/macro";
import {
  activityStatusSchema,
  type IRecurringActivitySchedule,
} from "@/api/Activities";
import styles from "./ActivitySchedule.module.scss";
import { EditButton } from "./EditButton";
import { Heading } from "@medoma/components/Heading/Heading";
import { useCurrentlyEditing } from "./useCurrentlyEditing";
import { EditContainer } from "./EditContainer";
import { Text } from "@medoma/components/Text/Text";
import { EditEndDate } from "./EditEndDate";
import type { IActivityStatus } from "@medoma/models/activities";
import { humanFormat } from "@medoma/models/date-and-time";

const DateHeading = () => (
  <Heading level="h3">
    <Trans>Datum</Trans>
  </Heading>
);

export const ActivityEndDate = ({
  activityId,
  status,
  schedule,
}: {
  activityId: string;
  status: IActivityStatus;
  schedule: IRecurringActivitySchedule;
}) => {
  const { t } = useLingui();
  const { currentlyEditing, setCurrentlyEditing } = useCurrentlyEditing();

  const seriesIsActive = status === activityStatusSchema.enum.active;

  const formattedTimespan = (binding: string) => {
    const startDate = humanFormat(schedule.start, {
      month: "long",
      day: "numeric",
    });

    if (schedule.end) {
      const endDate = humanFormat(schedule.end, {
        month: "long",
        day: "numeric",
      });
      return `${startDate}${binding}${endDate}`;
    }

    return `${startDate}, ${t`tills vidare`}`;
  };

  return (
    <section className={styles.informationSection}>
      {seriesIsActive ? (
        <>
          <div className={styles.editableSection}>
            <DateHeading />
            <EditButton
              attribute="edit-endDate"
              isEditing={currentlyEditing === "endDate"}
              toggleIsEditing={() =>
                setCurrentlyEditing(
                  currentlyEditing === "endDate" ? null : "endDate",
                )
              }
            />
          </div>
          {currentlyEditing === "endDate" ? (
            <EditContainer>
              <EditEndDate
                activityId={activityId}
                schedule={schedule}
                onSuccess={() => {
                  setCurrentlyEditing(null);
                }}
              />
            </EditContainer>
          ) : (
            <Text element="span">
              {formattedTimespan(` ${t`till och med`} `)}
            </Text>
          )}
        </>
      ) : (
        <>
          <DateHeading />
          <Text element="p">
            <Trans>Serien är avslutad</Trans>
          </Text>
        </>
      )}
    </section>
  );
};
